import React, { useEffect, useState, useRef } from 'react';
import { reportService } from '../../services';
import { createLogger } from '../../services/Debug';
function BackgroundUploader() {
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const processingRef = useRef(false);
  const log = createLogger('BackgroundUploader');
  useEffect(() => {
    if (!window.electronAPI) {
      log.error('[BackgroundUploader] electronAPI not available');
      return;
    }

    // Listen for auto-retrieve status
    const handleAutoRetrieveUpdated = (enabled) => {
      
      setAutoEnabled(enabled);
    };

    // Listen for upload trigger from Electron
    const handleTriggerUpload = async (files) => {
      
      setUploadQueue(files);
    };

    // Set up listeners
    window.electronAPI.onAutoRetrieveUpdated(handleAutoRetrieveUpdated);
    
    // Add new IPC listener for background uploads
    if (window.electronAPI.onTriggerBackgroundUpload) {
      window.electronAPI.onTriggerBackgroundUpload(handleTriggerUpload);
    }

    // Also react to full file list updates as a fallback trigger
    const handleFilesList = (files) => {
      if (autoEnabled && Array.isArray(files) && files.length > 0) {
        setUploadQueue(files);
      }
    };
    if (window.electronAPI.onMp3FilesList) {
      window.electronAPI.onMp3FilesList(handleFilesList);
    }

    // Request initial status and current files
    window.electronAPI.requestFileList();
    if (window.electronAPI.getAutoRetrieve) {
      window.electronAPI.getAutoRetrieve().then((enabled) => {
        setAutoEnabled(!!enabled);
      }).catch(() => {});
    }

    // Cleanup
    return () => {
      // Remove listeners if methods exist
      if (window.electronAPI.removeAutoRetrieveListener) {
        window.electronAPI.removeAutoRetrieveListener();
      }
      if (window.electronAPI.removeTriggerBackgroundUploadListener) {
        window.electronAPI.removeTriggerBackgroundUploadListener();
      }
    };
  }, []);

  // When auto is toggled on, ask for the current file list to kick things off
  // When auto is toggled off, clear any pending uploads
  useEffect(() => {
    if (autoEnabled && window.electronAPI && window.electronAPI.requestFileList) {
      window.electronAPI.requestFileList();
    } else if (!autoEnabled) {
      // Clear upload queue when auto-upload is disabled
      setUploadQueue([]);
    }
  }, [autoEnabled]);

  // Process upload queue
  useEffect(() => {
    if (uploadQueue.length > 0 && !processingRef.current && autoEnabled) {
      processUploads();
    }
  }, [uploadQueue, autoEnabled]);

  const processUploads = async () => {
    if (processingRef.current || !autoEnabled) return;
    
    
    processingRef.current = true;
    setIsUploading(true);

    try {
      for (const fileName of uploadQueue) {
        try {
          
          
          // Get file content from Electron
          const base64 = await window.electronAPI.readMp3File(fileName);
          if (!base64) {
            throw new Error('Failed to read file');
          }

          // Convert base64 to blob
          const byteCharacters = atob(base64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'audio/mpeg' });
          const file = new File([blob], fileName, { type: 'audio/mpeg' });

          // Upload using React service
          await reportService.uploadVoice(file);
          
          // Delete file after successful upload
          await window.electronAPI.deleteMp3File(fileName);
          
          
          
          // Notify Electron of success
          if (window.electronAPI.notifyUploadComplete) {
            window.electronAPI.notifyUploadComplete(fileName);
          }
        } catch (error) {
          log.error('[BackgroundUploader] Error uploading file:', fileName, error);
          
          // Notify Electron of error
          if (window.electronAPI.notifyUploadError) {
            window.electronAPI.notifyUploadError(fileName, error.message);
          }
        }
      }
    } finally {
      processingRef.current = false;
      setIsUploading(false);
      setUploadQueue([]);
      
      // Request updated file list
      window.electronAPI.requestFileList();
    }
  };

  // Minimal UI - this window is hidden
  return (
    <div style={{ padding: '20px', fontFamily: 'system-ui' }}>
      <h3>Background Uploader Service</h3>
      <p>Status: {autoEnabled ? 'Enabled' : 'Disabled'}</p>
      <p>Uploading: {isUploading ? 'Yes' : 'No'}</p>
      <p>Queue: {uploadQueue.length} files</p>
      <div style={{ marginTop: '20px', fontSize: '12px', color: '#666' }}>
        This window handles automatic dictaphone uploads in the background.
        It should remain hidden during normal operation.
      </div>
    </div>
  );
}

export default BackgroundUploader;